import api from "@/lib/axios";
import { API_ENDPOINTS } from "@/constants/api-endpoints";
import type {
  Driver,
  DriverKYC,
  DriverFilters,
  PaginatedResponse,
  PendingKYCResponse,
  PendingDriverAPIResponse,
} from "@/types";

export interface DriversParams extends DriverFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const driversApi = {
  getAll: async (
    params: DriversParams,
  ): Promise<PaginatedResponse<PendingDriverAPIResponse>> => {
    const { page, limit, search, status, kycStatus, vehicleType, state } = params;
    const response = await api.get(API_ENDPOINTS.DRIVERS.LIST, {
      params: {
        page,
        limit,
        search: search || undefined,
        status: status && status !== 'all' ? status : undefined,
        kycStatus: kycStatus && kycStatus !== 'all' ? kycStatus : undefined,
        vehicleType: vehicleType && vehicleType !== 'all' ? vehicleType : undefined,
        state: state && state !== 'all' ? state : undefined,
      },
    });
    // Map backend response { data: { drivers, total, page, pages } } to PaginatedResponse shape
    const rawData = response.data.data || response.data;
    return {
      data: rawData.drivers || [],
      pagination: {
        page: rawData.page || 1,
        limit: limit || 20,
        total: rawData.total || 0,
        totalPages: rawData.pages || 1,
      },
    };
  },

  getStates: async (): Promise<Array<{ state: string; count: number }>> => {
    const response = await api.get(API_ENDPOINTS.USER_STATES, { params: { role: 'driver' } });
    const raw = (response.data as any)?.data ?? response.data;
    return Array.isArray(raw) ? raw : [];
  },

  getById: async (id: string): Promise<PendingDriverAPIResponse> => {
    const response = await api.get(API_ENDPOINTS.DRIVERS.DETAIL(id));
    return response.data.data ? response.data.data : response.data;
  },

  getKYC: async (id: string): Promise<DriverKYC> => {
    const response = await api.get(API_ENDPOINTS.DRIVERS.KYC(id));
    return response.data;
  },

  approve: async (id: string): Promise<void> => {
    await api.put(API_ENDPOINTS.DRIVERS.APPROVE(id));
  },

  reject: async (id: string, reason: string): Promise<void> => {
    await api.put(API_ENDPOINTS.DRIVERS.REJECT(id), { reason });
  },

  suspend: async (id: string, reason: string): Promise<void> => {
    await api.put(API_ENDPOINTS.DRIVERS.SUSPEND(id), { reason });
  },

  activate: async (id: string): Promise<void> => {
    await api.put(API_ENDPOINTS.DRIVERS.ACTIVATE(id));
  },

  resetDriverData: async (id: string): Promise<{ message: string; cleared: Record<string, number> }> => {
    const response = await api.delete(API_ENDPOINTS.DRIVERS.RESET_DATA(id), {
      data: { confirm: "RESET_DRIVER" },
    });
    return response.data.data ?? response.data;
  },

  getPendingKYC: async (): Promise<PendingDriverAPIResponse[]> => {
    try {
      const response = await api.get<PendingKYCResponse>(
        API_ENDPOINTS.DRIVERS.PENDING_KYC,
      );
      return response.data.data || [];
    } catch (error) {
      throw error;
    }
  },
};

// Mock data
export const mockDrivers: Driver[] = Array.from({ length: 50 }, (_, i) => ({
  id: String(i + 1),
  name: [
    "Suresh Patel",
    "Ramesh Kumar",
    "Bikash Das",
    "Manoj Behera",
    "Sanjay Singh",
  ][i % 5],
  email: `driver${i + 1}@example.com`,
  phone: `98761${String(i).padStart(5, "0")}`,
  avatar: i % 3 === 0 ? `https://i.pravatar.cc/150?img=${i + 20}` : undefined,
  status: [
    "active",
    "active",
    "active",
    "inactive",
    "suspended",
    "pending_approval",
  ][i % 6] as Driver["status"],
  kycStatus: ["approved", "approved", "pending", "pending", "rejected"][
    i % 5
  ] as Driver["kycStatus"],
  vehicleType: ["bike", "scooty", "auto", "pickup", "mini_truck"][
    i % 5
  ] as Driver["vehicleType"],
  vehicleNumber: `OD-02-${String.fromCharCode(65 + (i % 26))}${String.fromCharCode(65 + ((i + 1) % 26))}-${1000 + i}`,
  rating: 3.5 + Math.random() * 1.5,
  totalTrips: Math.floor(Math.random() * 1000) + 50,
  totalEarnings: Math.floor(Math.random() * 200000) + 20000,
  isOnline: i % 3 === 0,
  currentLocation:
    i % 3 === 0
      ? { lat: 20.296 + Math.random() * 0.1, lng: 85.824 + Math.random() * 0.1 }
      : undefined,
  createdAt: new Date(
    Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000,
  ).toISOString(),
  lastActive: new Date(
    Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
  ).toISOString(),
  city: ["Bhubaneswar", "Cuttack", "Rourkela", "Brahmapur"][i % 4],
}));

export const mockPendingKYC: DriverKYC[] = mockDrivers
  .filter((d) => d.kycStatus === "pending")
  .slice(0, 10)
  .map((d) => ({
    id: `kyc-${d.id}`,
    driverId: d.id,
    driverName: d.name,
    phone: d.phone,
    email: d.email,
    status: d.kycStatus,
    submittedAt: new Date(
      Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
    ).toISOString(),
    documents: [
      {
        id: "1",
        type: "aadhaar_front",
        name: "Aadhaar Front",
        url: "https://placehold.co/400x250/3b82f6/ffffff?text=Aadhaar+Front",
        uploadedAt: new Date().toISOString(),
        status: "pending",
      },
      {
        id: "2",
        type: "aadhaar_back",
        name: "Aadhaar Back",
        url: "https://placehold.co/400x250/3b82f6/ffffff?text=Aadhaar+Back",
        uploadedAt: new Date().toISOString(),
        status: "pending",
      },
      {
        id: "3",
        type: "driving_license",
        name: "Driving License",
        url: "https://placehold.co/400x250/3b82f6/ffffff?text=DL",
        uploadedAt: new Date().toISOString(),
        status: "pending",
      },
      {
        id: "4",
        type: "vehicle_rc",
        name: "Vehicle RC",
        url: "https://placehold.co/400x250/3b82f6/ffffff?text=RC",
        uploadedAt: new Date().toISOString(),
        status: "pending",
      },
    ],
    personalInfo: {
      dateOfBirth: "1990-05-15",
      address: {
        address: "123, Main Street",
        city: "Bhubaneswar",
        state: "Odisha",
        pincode: "751001",
        lat: 20.296,
        lng: 85.824,
      },
      emergencyContact: "9876543210",
    },
    vehicleInfo: {
      type: d.vehicleType,
      number: d.vehicleNumber,
      make: ["Hero", "Honda", "TVS", "Bajaj", "Tata"][parseInt(d.id) % 5],
      model: ["Splendor", "Activa", "Apache", "Auto", "Ace"][
        parseInt(d.id) % 5
      ],
      year: 2018 + (parseInt(d.id) % 6),
      color: ["Black", "White", "Red", "Blue"][parseInt(d.id) % 4],
    },
  }));
